/**
 * Power Editor drawer (P4.T6).
 *
 * Plain `<textarea>` over the bundle JSON. Apply parses and stages
 * the result into composerStore.draftBundle via `replaceDraft` (single
 * `power-editor.dirty` marker); the Workshop top bar's Save commits.
 *
 * Design (plan §5 P4.T6 pins, 2026-05-21):
 *   - Plain <textarea> + JSON.parse on Apply (no Monaco / CodeMirror;
 *     "Wei's mode" = power-user, no autocomplete needed; zero new deps).
 *   - Apply-only — NOT a combined Apply+Save. Two-step matches the
 *     card pattern (cards stage; the Workshop top bar commits).
 *   - Cards stay editable when drawer is open (last-edit-wins).
 *   - Disabled Apply when serverBundle is null (defensive — drawer
 *     should be unreachable before the Workshop has loaded a bundle).
 */
import { useEffect, useState } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@/components/ui/Modal'
import { Button, Alert, AlertDescription } from '@/components/ui'
import { useComposerStore } from '@/stores/composerStore'
import type { PersonaBundleResponse } from '@/types/personaWizard'

interface PowerEditorDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PowerEditorDrawer({ open, onOpenChange }: PowerEditorDrawerProps) {
  const serverBundle = useComposerStore((s) => s.serverBundle)
  const draftBundle = useComposerStore((s) => s.draftBundle)
  const replaceDraft = useComposerStore((s) => s.replaceDraft)

  const seed = draftBundle ?? serverBundle
  const initial = seed ? JSON.stringify(seed, null, 2) : ''
  const [text, setText] = useState(initial)
  const [error, setError] = useState<string | null>(null)

  // Re-seed when the drawer transitions from closed → open so subsequent
  // card edits land in the textarea on next open.
  useEffect(() => {
    if (open) {
      setText(initial)
      setError(null)
    }
    // We intentionally re-read `initial` only when `open` flips — the
    // user's in-flight edits during a single open session should not be
    // clobbered by an interleaved card edit (last-edit-wins per §5 pin).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleApply() {
    const trimmed = text.trim()
    if (!trimmed) {
      setError('Bundle JSON is empty.')
      return
    }
    let parsed: PersonaBundleResponse
    try {
      parsed = JSON.parse(trimmed) as PersonaBundleResponse
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Invalid JSON'
      setError(`JSON parse error: ${msg}`)
      return
    }
    replaceDraft(parsed)
    onOpenChange(false)
  }

  const applyDisabled = serverBundle === null

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>Power Editor</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Edit the persona bundle as raw JSON. Apply stages your
              change into the Workshop&apos;s draft — use Save on the
              top bar to commit.
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full h-96 font-mono text-xs p-3 rounded-sm border border-border bg-muted text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
              spellCheck={false}
              disabled={applyDisabled}
            />
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={applyDisabled}>
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
